import React, { useState, useEffect, useRef } from 'react';
import { FaArrowLeft, FaPlus, FaSearch, FaEdit, FaTrash } from "react-icons/fa";
import '@styles/GeneralPages.css';
import '@styles/ExerciseManagement.css';
import { getIndexedDBData, saveExerciseMuscles, deleteExerciseFromDB, getExerciseMuscles } from '@utility/workout_utils.js';
import { deleteIndexedDBData, addIndexedDBData } from '@utility/indexedDB_utils';
import MuscleSlider from '@components/MuscleSlider';
import { getColorFromId } from '@components/MuscleSlider';
import { openDB }  from '@utility/indexedDB_utils';

const ExerciseManagement = () => {
  // State for exercise management
  const [exercises, setExercises] = useState([]);
  const [showAddExerciseModal, setShowAddExerciseModal] = useState(false);
  const [exerciseSearchTerm, setExerciseSearchTerm] = useState('');
  const [newExerciseData, setNewExerciseData] = useState({
    name: '',
    is_isolated: false,
    category: 'Freeweight',
    customCategory: '',
    primaryMuscles: [],
    secondaryMuscles: []
  });
  const [muscles, setMuscles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // current page in exercise form
  const [editingExercise, setEditingExercise] = useState(null);
  const totalPages = 3; // for the exercise form
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [percentageError, setPercentageError] = useState(null);
  
  // Reference for exercise list scrolling
  const exerciseListRef = useRef(null);

  // Filter exercises based on search term
  const filteredExercises = exercises.filter(exercise =>
    exercise.name.toLowerCase().includes(exerciseSearchTerm.toLowerCase())
  );

  // Load exercises and muscles on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const db = await openDB();
        
        // Load exercises
        const exercisesData = await getIndexedDBData('exercises');
        setExercises(exercisesData);
        
        // Load muscles for selection
        const musclesData = await getIndexedDBData('muscles');
        setMuscles(musclesData);
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load exercise data");
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Open the add exercise modal
  const openAddExerciseModal = () => {
    setShowAddExerciseModal(true);
    setNewExerciseData({
      name: '',
      is_isolated: false,
      category: 'Freeweight',
      customCategory: '',
      primaryMuscles: [],
      secondaryMuscles: []
    });
    setCurrentPage(1); // Reset to first page
    setEditingExercise(null);
  };

  const updateMusclePercentage = (muscleId, type, percent) => {
    setNewExerciseData(prev => {
      const newMuscles = type === 'primary' 
        ? [...prev.primaryMuscles]
        : [...prev.secondaryMuscles];
        
      const index = newMuscles.findIndex(m => m.muscle_id === muscleId);
      if (index >= 0) {
        newMuscles[index] = { ...newMuscles[index], percent };
      }
      
      return {
        ...prev,
        [type === 'primary' ? 'primaryMuscles' : 'secondaryMuscles']: newMuscles
      };
    });
  };

  const validatePercentages = () => {
    const primaryTotal = newExerciseData.primaryMuscles.reduce((sum, m) => sum + m.percent, 0);
    const secondaryTotal = newExerciseData.secondaryMuscles.reduce((sum, m) => sum + m.percent, 0);
    const combinedTotal = primaryTotal + secondaryTotal;
    
    if (combinedTotal != 100) {
      setPercentageError(`Total must be exactly 100% (currently ${combinedTotal.toFixed(1)}%)`);
      return false;
    }
    
    setPercentageError(null);
    return true;
  };

  const toggleMuscleSelection = (muscleId, type) => {
    setNewExerciseData(prev => {
      const oppositeType = type === 'primary' ? 'secondaryMuscles' : 'primaryMuscles';
      const currentType = type === 'primary' ? 'primaryMuscles' : 'secondaryMuscles';
      
      // Check if already selected in current type
      const existingIndex = prev[currentType].findIndex(m => m.muscle_id === muscleId);
      
      if (existingIndex >= 0) {
        // Remove from current type
        return {
          ...prev,
          [currentType]: prev[currentType].filter(m => m.muscle_id !== muscleId)
        };
      } else {
        // Remove from opposite type if exists
        const newOpposite = prev[oppositeType].filter(m => m.muscle_id !== muscleId);
        
        // Add to current type with default 0% (will be calculated later)
        return {
          ...prev,
          [oppositeType]: newOpposite,
          [currentType]: [
            ...prev[currentType],
            { muscle_id: muscleId, percent: 0 }
          ]
        };
      }
    });
  };

  const distributePercentages = () => {
    setNewExerciseData(prev => {
      // Only distribute if percentages are 0 (new exercise)
      const needsDistribution = prev.primaryMuscles.some(m => m.percent === 0) || 
                              prev.secondaryMuscles.some(m => m.percent === 0);

      if (!needsDistribution) {
        // Keep existing percentages
        return prev;
      }

      // Calculate primary muscle percentages (70% total)
      const primaryCount = prev.primaryMuscles.length;
      const primaryPercent = primaryCount > 0 ? 70 / primaryCount : 0;
      
      // Calculate secondary muscle percentages (30% total)
      const secondaryCount = prev.secondaryMuscles.length;
      const secondaryPercent = secondaryCount > 0 ? 30 / secondaryCount : 0;
      
      return {
        ...prev,
        primaryMuscles: prev.primaryMuscles.map(m => ({
          ...m,
          percent: primaryPercent
        })),
        secondaryMuscles: prev.secondaryMuscles.map(m => ({
          ...m,
          percent: secondaryPercent
        }))
      };
    });
    setCurrentPage(3);
  };

  // Open edit modal for existing exercise
  const openEditExerciseModal = async (exercise) => {
    try {
      const { primary, secondary } = await getExerciseMuscles(exercise.id);
      
      setShowAddExerciseModal(true);
      setNewExerciseData({
        name: exercise.name,
        is_isolated: exercise.is_isolated,
        category: ['Freeweight', 'Machine', 'Cable', 'Bodyweight'].includes(exercise.category) 
          ? exercise.category 
          : 'other',
        customCategory: ['Freeweight', 'Machine', 'Cable', 'Bodyweight'].includes(exercise.category) 
          ? '' 
          : exercise.category,
        primaryMuscles: primary.map(p => ({ ...p })),
         secondaryMuscles: secondary.map(s => ({ ...s }))
      });
      setEditingExercise(exercise.id);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error loading exercise muscles:", error);
      setError("Failed to load exercise details");
    }
  };

  const handlePercentageChange = (muscleId, type, newPercent) => {
  setNewExerciseData(prev => {
    const updatedMuscles = prev[type === 'primary' ? 'primaryMuscles' : 'secondaryMuscles']
      .map(m => m.muscle_id === muscleId ? {...m, percent: newPercent} : m);
    
    return {
      ...prev,
      [type === 'primary' ? 'primaryMuscles' : 'secondaryMuscles']: updatedMuscles
    };
  });
};

  // Close the modal
  const closeModal = () => {
    setShowAddExerciseModal(false);
    setCurrentPage(1); // Reset to first page
  };

   // Delete (rusure you wanna delete exercise)
  const openDeleteModal = (exerciseId) => {
    setExerciseToDelete(exerciseId);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setExerciseToDelete(null);
  };

  // Handle deleting an exercise
  const handleDeleteExercise = async () => {
    try {
      await deleteExerciseFromDB(exerciseToDelete);
      setExercises(exercises.filter(ex => ex.id !== exerciseToDelete));
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting exercise:", error);
      setError("Failed to delete exercise");
      closeDeleteModal();
    }
  };

  // Handle creating/updating a custom exercise
  const handleSaveExercise = async () => {
    try {
      if (!newExerciseData.name.trim()) {
        setError("Exercise name is required");
        return;
      }

      if (!validatePercentages()) {
        return;
      }
      
      const db = await openDB();
      let exerciseId;
      
      if (editingExercise) {
        // Update existing exercise
        await new Promise((resolve, reject) => {
          const transaction = db.transaction(["exercises"], "readwrite");
          const exerciseStore = transaction.objectStore("exercises");
          
          const updatedExercise = {
            id: editingExercise,
            name: newExerciseData.name.trim(),
            is_isolated: newExerciseData.is_isolated,
            category: newExerciseData.category === 'other' ? 
              newExerciseData.customCategory : 
              newExerciseData.category
          };
          
          const request = exerciseStore.put(updatedExercise);
          
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(new Error("Failed to update exercise"));
        });
        
        exerciseId = editingExercise;
      } else {
        // Add new exercise
        exerciseId = await new Promise((resolve, reject) => {
          const transaction = db.transaction(["exercises"], "readwrite");
          const exerciseStore = transaction.objectStore("exercises");
          
          const newExercise = {
            name: newExerciseData.name.trim(),
            is_isolated: newExerciseData.is_isolated,
            category: newExerciseData.category === 'other' ? 
              newExerciseData.customCategory : 
              newExerciseData.category
          };
          
          const request = exerciseStore.add(newExercise);
          
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(new Error("Failed to create exercise"));
        });
      }
      
      // Save muscle associations if needed
      if (newExerciseData.primaryMuscles.length > 0 || newExerciseData.secondaryMuscles.length > 0) {
        // Clear existing associations
        await Promise.all([
          deleteIndexedDBData('exercise_primary_muscles', exerciseId, 'exercise_id'),
          deleteIndexedDBData('exercise_secondary_muscles', exerciseId, 'exercise_id')
        ]);

        // Add new associations with percentages
        await Promise.all([
          ...newExerciseData.primaryMuscles.map(muscle => 
            addIndexedDBData('exercise_primary_muscles', {
              exercise_id: exerciseId,
              muscle_id: muscle.muscle_id,
              percent: muscle.percent
            })
          ),
          ...newExerciseData.secondaryMuscles.map(muscle => 
            addIndexedDBData('exercise_secondary_muscles', {
              exercise_id: exerciseId,
              muscle_id: muscle.muscle_id,
              percent: muscle.percent
            })
          )
        ]);
      }
      
      // Update local exercises state
      if (editingExercise) {
        setExercises(exercises.map(ex => 
          ex.id === editingExercise ? {
            ...ex,
            name: newExerciseData.name.trim(),
            is_isolated: newExerciseData.is_isolated,
            category: newExerciseData.category === 'other' ? 
              newExerciseData.customCategory : 
              newExerciseData.category
          } : ex
        ));
      } else {
        const updatedExercises = [
          ...exercises,
          {
            id: exerciseId,
            name: newExerciseData.name.trim(),
            is_isolated: newExerciseData.is_isolated,
            category: newExerciseData.category === 'other' ? 
              newExerciseData.customCategory : 
              newExerciseData.category
          }
        ];
        setExercises(updatedExercises);
      }
      
      closeModal();
    } catch (error) {
      console.error("Error saving exercise:", error);
      setError(`Failed to save exercise: ${error.message}`);
    }
  };

  // Handle form navigation
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Render the current form page
  const renderFormPage = () => {
    switch (currentPage) {
      case 1:
        return (
          <div className="form-page">
            <h3>{editingExercise ? `Edit ${newExerciseData.name}` : 'Create New Exercise'}</h3>
            <div className="form-group">
              <label>Exercise Type:</label>
              <div
                className={`toggle-switch ${newExerciseData.is_isolated ? 'compound' : 'compoundisolated'}`}
                onClick={() => setNewExerciseData({
                  ...newExerciseData,
                  is_isolated: !newExerciseData.is_isolated
                })}
                role="switch"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setNewExerciseData({
                      ...newExerciseData,
                      is_isolated: !newExerciseData.is_isolated
                    });
                  }
                }}
                aria-checked={newExerciseData.is_isolated}
              >
                <span className="label-isolated">Isolated</span>
                <span className="toggle-thumb"></span>
                <span className="label-compound">Compound</span>
              </div>
            </div>

            <div className="form-group">
              <label>Exercise Name:</label>
              <input
                type="text"
                value={newExerciseData.name}
                onChange={(e) => setNewExerciseData({
                  ...newExerciseData,
                  name: e.target.value
                })}
                placeholder="Enter exercise name"
                className="exercise-name-input"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Category:</label>
              <select
                value={newExerciseData.category}
                onChange={(e) => setNewExerciseData({
                  ...newExerciseData,
                  category: e.target.value
                })}
                className="exercise-name-input"
              >
                <option value="Freeweight">Freeweight</option>
                <option value="Machine">Machine</option>
                <option value="Cable">Cable</option>
                <option value="Bodyweight">Bodyweight</option>
                <option value="other">Other (specify)</option>
              </select>

              {newExerciseData.category === 'other' && (
                <input
                  type="text"
                  placeholder="Enter custom category"
                  value={newExerciseData.customCategory || ''}
                  onChange={(e) => setNewExerciseData({
                    ...newExerciseData,
                    customCategory: e.target.value
                  })}
                />
              )}
            </div>

            <div className="button-box">
              <button onClick={closeModal} className="cancel-button">Cancel</button>
              <button onClick={goToNextPage} className="edit-button" disabled={!newExerciseData.name.trim()}>Next</button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="form-page">
            <h2>Muscles engaged in {newExerciseData.name} </h2>
            <div className="muscle-selection-section">
              <h3>Select Primary Muscles</h3>
              <div className="muscle-grid">
                {muscles.map(muscle => {
                  const isPrimary = newExerciseData.primaryMuscles.some(m => m.muscle_id === muscle.id);
                  return (
                    <div 
                      key={`primary-${muscle.id}`}
                      className={`muscle-option-p ${isPrimary ? 'selected' : ''}`}
                      onClick={() => toggleMuscleSelection(muscle.id, 'primary')}
                    >
                      {muscle.name}
                    </div>
                  );
                })}
              </div>

              <h3>Select Secondary Muscles</h3>
              <div className="muscle-grid">
                {muscles.map(muscle => {
                  const isSecondary = newExerciseData.secondaryMuscles.some(m => m.muscle_id === muscle.id);
                  return (
                    <div 
                      key={`secondary-${muscle.id}`}
                      className={`muscle-option-s ${isSecondary ? 'selected' : ''}`}
                      onClick={() => toggleMuscleSelection(muscle.id, 'secondary')}
                    >
                      {muscle.name}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="button-box">
              <button onClick={goToPreviousPage} className="cancel-button">
                Back
              </button>
              <button 
                onClick={distributePercentages} 
                className="edit-button"
                disabled={newExerciseData.primaryMuscles.length === 0 && 
                          newExerciseData.secondaryMuscles.length === 0}
              >
                Next
              </button>
            </div>
          </div>
        );
      case 3:
        const primaryTotal = newExerciseData.primaryMuscles.reduce((sum, m) => sum + m.percent, 0);
        const secondaryTotal = newExerciseData.secondaryMuscles.reduce((sum, m) => sum + m.percent, 0);
        const combinedTotal = primaryTotal + secondaryTotal;

        return (
          // <div className="modal-wrapper">
          <div className="form-page">
            
            <h3>{newExerciseData.name} muscle engagement</h3>
            
            <div className="totals-display">
              <div>Primary: {primaryTotal.toFixed(1)}%</div>
              <div>Secondary: {secondaryTotal.toFixed(1)}%</div>
              <div className={combinedTotal != 100 ? 'combined' : ''}>
                Combined: {combinedTotal.toFixed(1)}%
              </div>
            </div>

            <MuscleSlider
              muscles={muscles}
              primaryMuscles={newExerciseData.primaryMuscles}
              secondaryMuscles={newExerciseData.secondaryMuscles}
              onPrimaryChange={(newPrimary) => setNewExerciseData(prev => ({
                ...prev,
                primaryMuscles: newPrimary
              }))}
              onSecondaryChange={(newSecondary) => setNewExerciseData(prev => ({
                ...prev,
                secondaryMuscles: newSecondary
              }))}
            />

            {percentageError && (
              <div className="percentage-error">
                {percentageError}
              </div>
            )}

            <div className="button-box">
              <button onClick={() => setCurrentPage(2)} className="cancel-button">
                Back
              </button>
              <button onClick={handleSaveExercise} disabled={combinedTotal != 100} className="edit-button">
                {editingExercise ? 'Update Exercise' : 'Create Exercise'}
              </button>
            </div>
          </div>
          // </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="settings-content">
      <h2>Exercise Management</h2> 
      <div className="group-card">
        <div className="button-box">
          <button className="new-button wide-button" onClick={openAddExerciseModal}><FaPlus /> Add New Exercise</button>
        </div>
      </div>

      <div className="section">
        <h2>Exercises</h2>
        <div className="exercise-search-container">
          <div className="search-input-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search exercises..."
              value={exerciseSearchTerm}
              onChange={(e) => setExerciseSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="exercises-list" ref={exerciseListRef}>
          {isLoading ? (
            <p>Loading exercises...</p>
          ) : error ? (
            <p className="error-message">{error}</p>
          ) : filteredExercises.length === 0 ? (
            <p>No exercises found. {exerciseSearchTerm ? 'Try a different search.' : 'Add your first exercise.'}</p>
          ) : (
            <table className="exercises-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th className="actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExercises.map(exercise => (
                  <tr key={exercise.id} className="exercise-item">
                    <td>{exercise.name}</td>
                    <td>
                      {exercise.category == "Freeweight" ? "FW" : 
                      exercise.category == "Bodyweight" ? "BW" :
                      exercise.category == "Machine" ? "MCH" :
                      exercise.category == "Cable" ? "CAB" :
                      exercise.category == "Cardio" ? "CAD" : "OTH"}
                    </td>
                    <td>{exercise.is_isolated ? 'I' : 'C'}</td>
                    <td className="actions-cell">
                      <button 
                        onClick={() => openEditExerciseModal(exercise)}
                        className="edit-button"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => openDeleteModal(exercise.id)}
                        className="remove-button"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Exercise Modal */}
      {showAddExerciseModal && (
        <div className="modal-overlay">
          <div className="modal-wrapper">
            <div className="modal-content">
              {renderFormPage()}
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-wrapper">
          <div className="modal-content">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this exercise?</p>
            <p>This action cannot be undone, and will remove exercise from your workout log.</p>
            
            <div className="button-box">
              <button
                onClick={closeDeleteModal}
                className="edit-button"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteExercise}
                className="remove-button"
              >
                Delete Exercise
              </button>
            </div>
          </div>
          </div>
        </div>
      )}

   </div>
  );
};

export default ExerciseManagement;